import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiUrl } from '../../config/apiBase';
import { useAuth } from '../../AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { MdCameraAlt } from 'react-icons/md';
import Sidebar from '../Sidebar';
import Feedback from '../Feedback';
import Footer from '../../Landing Page/Footer';
import './PersonalizedAnswer.css';

// Backend mounts image routes at /api/images (see index.js)
const JOB_IMAGE_BASE = apiUrl('/api/images/job');

const PersonalizedAnswer = () => {
    const location = useLocation();
    const { refreshTokenIfNeeded } = useAuth();
    const questions = location.state?.questions || [];
    console.log('Questions:', questions);

    // Get jobId from URL params or state
    const params = new URLSearchParams(location.search);
    const jobId = location.state?.jobId || params.get("job_id");
    console.log('jobId:', jobId);

    const [textInput, setTextInput] = useState('');
    const [imageData, setImageData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [conversation, setConversation] = useState([]);
    const [currentMessage, setCurrentMessage] = useState('');
    const [sessionId, setSessionId] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [sketchSent, setSketchSent] = useState(false);
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentTool, setCurrentTool] = useState('pen'); // 'pen' or 'eraser'
    const [currentColor, setCurrentColor] = useState('#000000');
    const [brushSize, setBrushSize] = useState(2);
    const [uploadedImages, setUploadedImages] = useState([]);
    const fileInputRef = useRef(null);

    // Handle unhandled promise rejections (often caused by browser extensions)
    useEffect(() => {
        const handleUnhandledRejection = (event) => {
            // Suppress common browser extension errors
            if (event.reason && (
                event.reason.message?.includes('message channel closed') ||
                event.reason.message?.includes('Extension context invalidated') ||
                event.reason.name === 'AbortError'
            )) {
                event.preventDefault();
                return;
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        
        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    // Cleanup uploaded image URLs on unmount
    useEffect(() => {
        return () => {
            uploadedImages.forEach(image => {
                if (image.url) {
                    URL.revokeObjectURL(image.url);
                }
            });
        };
    }, [uploadedImages]);

    const fetchImage = useCallback(async () => {
        if (!jobId) return;
        
        try {
            const token = await refreshTokenIfNeeded();
            
            const resp = await fetch(`${JOB_IMAGE_BASE}/${jobId}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            
            if (resp.ok) {
                const data = await resp.json();
                setImageData(data);
                console.log('Fetched image data:', data);
            } else {
                console.error('Failed to fetch image:', resp.status);
            }
        } catch (e) {
            // Only log error if it's not an abort error
            if (e.name !== 'AbortError') {
                console.error('Error fetching image:', e);
            }
        }
    }, [jobId]);

    // Check sessionStorage for cached image first
    useEffect(() => {
        if (!jobId || imageData) return;
        try {
            const cached = sessionStorage.getItem('pendingImage');
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed?.job_id === jobId) {
                    setImageData(parsed);
                    console.log('Loaded image from sessionStorage:', parsed);
                }
            }
        } catch (e) {
            console.error('Error loading from sessionStorage:', e);
        }
    }, [jobId, imageData]);

    // Listen for imageLoaded events from other components
    useEffect(() => {
        const handleImageLoaded = (event) => {
            console.log('Received imageLoaded event with data:', event.detail);
            setImageData(event.detail.imageData);
        };

        window.addEventListener('imageLoaded', handleImageLoaded);

        return () => {
            window.removeEventListener('imageLoaded', handleImageLoaded);
        };
    }, []);

    // Check window.currentImage as fallback
    useEffect(() => {
        if (!imageData && window.currentImage) {
            console.log('Setting imageData from window.currentImage');
            setImageData(window.currentImage);
        }
    }, [imageData]);

    useEffect(() => {
        fetchImage();
    }, [fetchImage]);

    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) return;

        // Clear the canvas to white
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }, []);

    const startDrawing = (e) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        
        // Set drawing properties based on current tool
        if (currentTool === 'eraser') {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.strokeStyle = 'rgba(0,0,0,1)'; // Eraser uses white
            ctx.lineWidth = brushSize * 3; // Eraser is larger
        } else {
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = currentColor;
            ctx.lineWidth = brushSize;
        }
        
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        const x = (e.clientX - rect.left) * (canvas.width / rect.width);
        const y = (e.clientY - rect.top) * (canvas.height / rect.height);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) * (canvas.width / rect.width);
        const y = (e.clientY - rect.top) * (canvas.height / rect.height);
        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const handleImageUpload = (event) => {
        const files = Array.from(event.target.files);
        const imageFiles = files.filter(file => file.type.startsWith('image/'));

        if (imageFiles.length === 0) {
            alert('Please select image files only.');
            return;
        }

        const newImages = imageFiles.map(file => ({
            file,
            url: URL.createObjectURL(file),
            name: file.name,
            id: Date.now() + Math.random()
        }));

        setUploadedImages(prev => [...prev, ...newImages]);
    };

    const removeUploadedImage = (imageId) => {
        setUploadedImages(prev => {
            const imageToRemove = prev.find(img => img.id === imageId);
            if (imageToRemove) {
                URL.revokeObjectURL(imageToRemove.url);
            }
            return prev.filter(img => img.id !== imageId);
        });
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    // Enhanced function to create clean, readable math content without LaTeX symbols
    const preprocessMathContent = (content) => {
        if (!content) return content;
        
        let processedContent = content;
        
        // Step 1: Clean up malformed expressions and HTML remnants
        processedContent = processedContent.replace(/\$\$\s*\d+\s*\$/g, '');
        processedContent = processedContent.replace(/\$\s*\d+\s*\$/g, '');
        processedContent = processedContent.replace(/\$\$\s*\$\$/g, '');
        processedContent = processedContent.replace(/\$\s*\$/g, '');
        processedContent = processedContent.replace(/:\s*\\\s*\$\$1\$/g, ':');
        processedContent = processedContent.replace(/1\s*1/g, '');
        processedContent = processedContent.replace(/\\\s*$/gm, '');
        
        // Remove HTML tags and classes completely
        processedContent = processedContent.replace(/<[^>]*>/g, '');
        processedContent = processedContent.replace(/class\s*=\s*["'][^"']*["']/g, '');
        processedContent = processedContent.replace(/div\s*=\s*["'][^"']*["']/g, '');
        
        // Step 2: Clean LaTeX formatting
        processedContent = processedContent
            .replace(/\$\$([^$]+)\$\$/g, '$1')
            .replace(/\$([^$]+)\$/g, '$1')
            .replace(/\\(mu|tau|alpha|beta|gamma|delta|sigma|theta|pi|omega)/g, '$1')
            .replace(/\\cdot/g, '·')
            .replace(/\\times/g, '×')
            .replace(/\\[a-zA-Z]+/g, '')
            .replace(/([A-Za-z])_\{([A-Za-z0-9]+)\}/g, '$1_$2')
            .replace(/([A-Za-z])\^\{([A-Za-z0-9]+)\}/g, '$1^$2')
            .replace(/[{}]/g, '')
            // Remove markdown bold
            .replace(/\*\*(.*?)\*\*/g, '$1');
        
        // Step 3: Fix broken structure
        processedContent = processedContent
            // Fix broken bullet points like "Step • by • Step"
            .replace(/Step\s*•\s*by\s*•\s*Step/gi, 'Step-by-Step')
            .replace(/•\s*\n\s*•/g, ' ')
            .replace(/•\s+([a-z])/g, '• $1')
            // Clean up equations and put them on proper lines
            .replace(/\\\s*([A-Za-z_]+\s*=\s*[^\\]+)/g, '\n\n    $1')
            .replace(/\\\s*/g, '\n\n    ')
            // Fix section headers
            .replace(/([A-Z][a-zA-Z\s]*):(?!\s*\n)/g, '\n\n$1:\n')
            // Clean bullet points
            .replace(/\n\s*•\s*/g, '\n• ')
            // Fix "where" clauses
            .replace(/,\s*where\s+/g, '\n\nwhere ')
            .replace(/\)\s*,\s*where/g, ')\n\nwhere')
            // Clean parentheses
            .replace(/\(\s*([^)]+)\s*\)/g, '($1)')
            // Clean spacing
            .replace(/\n{3,}/g, '\n\n')
            .replace(/\s{2,}/g, ' ');
        
        // Step 4: Structure properly
        processedContent = processedContent
            // Add section breaks with bold headers
            .replace(/(Derivation|Step-by-Step|Formula|Variables|Summary|Equilibrium Conditions):/gi, '\n\n**$1:**\n')
            .replace(/Final Formula/gi, '\n\n**Final Formula:**\n')
            // Convert bullet points to markdown
            .replace(/•/g, '-')
            // Break sentences to new lines for better readability
            .replace(/([.!?])\s+/g, '$1\n\n')
            // Ensure numbered lists start on new lines
            .replace(/(\d+\.)\s*/g, '\n\n$1 ')
            // Fix equations - ensure they're indented
            .replace(/\n\s*([A-Za-z_]+\s*=\s*[^\n]+)/g, '\n\n    $1')
            // Clean final formatting
            .replace(/\n\n+/g, '\n\n')
            .replace(/\(\s*$/g, '')
            .replace(/\s*,\s*$/g, '')
            .trim();
        
        return processedContent;
    };

    const handleSubmit = async () => {
        if (!textInput.trim()) return;

        setLoading(true);
        
        try {
            const token = await refreshTokenIfNeeded();

            const formData = new FormData();
            formData.append('questions', JSON.stringify(questions));
            formData.append('text', textInput);

            // Add uploaded images
            uploadedImages.forEach((image, index) => {
                formData.append(`uploaded_image_${index}`, image.file);
            });

            // Add canvas drawing if it exists
            if (canvasRef.current) {
                canvasRef.current.toBlob((blob) => {
                    if (blob && blob.size > 100) { // Only send if there's actual drawing content
                        formData.append('sketch', blob, 'sketch.png');
                        setSketchSent(true);
                    }
                    
                    // Add original generated image if it exists
                    if (imageData?.image_url) {
                        formData.append('original_image_url', imageData.image_url);
                    }
                    
                    sendFormData(formData, token);
                }, 'image/png');
            } else {
                // Add original generated image if it exists
                if (imageData?.image_url) {
                    formData.append('original_image_url', imageData.image_url);
                }
                sendFormData(formData, token);
            }
        } catch (err) {
            console.error('Error:', err);
            const errorMessage = err.response?.data?.error || err.message || 'Failed to submit';
            alert('Failed to submit: ' + errorMessage);
            setLoading(false);
        }
    };

    const sendFormData = async (formData, token) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
                ...(token && { Authorization: `Bearer ${token}` })
            }
        };

        const response = await axios.post(`${apiUrl('/api/personalized-answer')}`, formData, config);

        setConversation([{ 
            role: 'user', 
            content: `${textInput}${sketchSent ? ' (with sketch)' : ''}${imageData ? ' (with generated image)' : ''}` 
        }, { 
            role: 'ai', 
            content: response.data.response 
        }]);
        setSessionId(response.data.sessionId);
        setSubmitted(true);
        setLoading(false);
    };

    const handleSendMessage = async () => {
        if (!currentMessage.trim()) return;

        const newConversation = [...conversation, { role: 'user', content: currentMessage }];
        setConversation(newConversation);
        setCurrentMessage('');

        try {
            const token = await refreshTokenIfNeeded();

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` })
                }
            };

            const payload = {
                conversation: newConversation,
                questions: questions,
                sessionId: sessionId
            };

            const response = await axios.post(`${apiUrl('/api/personalized-answer/conversation')}`, payload, config);

            setConversation([...newConversation, { role: 'ai', content: response.data.response }]);
        } catch (err) {
            console.error('Error:', err);
            const errorMessage = err.response?.data?.error || err.message || 'Failed to send message';
            alert('Failed to send message: ' + errorMessage);
        }
    };

    return (
        <div className="pa-container">
            <Sidebar activeItem="personalized" />
            
            <div className="pa-main">
                <div className="pa-header">
                    <h2>Personalized Answers</h2>
                    <p>Provide your response and get personalized feedback.</p>
                </div>
                
                <div className="pa-body">
                    <div className="pa-grid">
                        <div className="pa-left">
                            {/* Questions Section */}
                            <div className="pa-questions">
                                <h4 className="ap-section-title">Interview Questions</h4>
                                <ul className="pa-questions-list">
                                    {questions.map((q, index) => (
                                        <li key={index} className="pa-question-item">
                                            {typeof q === 'string' ? q : q.question}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {/* Canvas Section */}
                            <div className="pa-canvas">
                                <h4 className="ap-section-title">Sketch Your Ideas</h4>
                                
                                {/* Drawing Tools */}
                                <div className="pa-drawing-tools">
                                    <div className="pa-tool-buttons">
                                        <button 
                                            className={`pa-tool-btn ${currentTool === 'pen' ? 'active' : ''}`}
                                            onClick={() => setCurrentTool('pen')}
                                            title="Pen Tool"
                                        >
                                            ✏️
                                        </button>
                                        <button 
                                            className={`pa-tool-btn ${currentTool === 'eraser' ? 'active' : ''}`}
                                            onClick={() => setCurrentTool('eraser')}
                                            title="Eraser Tool"
                                        >
                                            🧽
                                        </button>
                                    </div>
                                    
                                    <div className="pa-color-palette">
                                        {['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500'].map(color => (
                                            <button
                                                key={color}
                                                className={`pa-color-btn ${currentColor === color ? 'active' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => setCurrentColor(color)}
                                                title={`Color: ${color}`}
                                            />
                                        ))}
                                    </div>
                                    
                                    <div className="pa-brush-controls">
                                        <label className="pa-brush-label">Size:</label>
                                        <input
                                            type="range"
                                            min="1"
                                            max="20"
                                            value={brushSize}
                                            onChange={(e) => setBrushSize(parseInt(e.target.value))}
                                            className="pa-brush-slider"
                                            title={`Brush size: ${brushSize}px`}
                                        />
                                        <span className="pa-brush-size">{brushSize}px</span>
                                    </div>
                                </div>
                                
                                <canvas
                                    ref={canvasRef}
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={(e) => {
                                        e.preventDefault();
                                        const touch = e.touches[0];
                                        startDrawing(touch);
                                    }}
                                    onTouchMove={(e) => {
                                        e.preventDefault();
                                        const touch = e.touches[0];
                                        draw(touch);
                                    }}
                                    onTouchEnd={stopDrawing}
                                    className="pa-drawing-canvas"
                                    width="400"
                                    height="300"
                                />
                                <button 
                                    onClick={clearCanvas}
                                    className="pa-clear-canvas-btn"
                                >
                                    Clear Canvas
                                </button>
                            </div>
                        </div>

                        <div className="pa-right">
                            {/* Image Display Section */}
                            {imageData && (
                                <div className="pa-image">
                                    <h4 className="ap-section-title">Technical Scenario</h4>
                                    <img 
                                        src={imageData.image_url} 
                                        alt="Generated scenario"
                                        className="pa-scenario-image"
                                    />
                                </div>
                            )}

                            {/* Conditional: Text Input or Conversation */}
                            {submitted ? (
                                <div className="pa-text-input">
                                    <h4 className="ap-section-title">Personalized Feedback</h4>
                                    <div className="pa-conversation-content">
                                        {conversation.map((msg, index) => (
                                            <div key={index} className={`pa-message ${msg.role}`}>
                                                <strong>
                                                    {msg.role === 'user' ? 'Your Response' : 'Feedback & Analysis'}
                                                </strong> 
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkMath]}
                                                    rehypePlugins={[rehypeKatex]}
                                                    components={{
                                                        // Handle display math with proper overflow control
                                                        div: ({ children, className }) => {
                                                            if (className === 'math-display') {
                                                                return <div className="math-display-container" style={{overflowX: 'hidden', maxWidth: '100%'}}>{children}</div>;
                                                            }
                                                            return <div className={className} style={{maxWidth: '100%', overflowX: 'hidden'}}>{children}</div>;
                                                        },
                                                        // Handle paragraphs with better text wrapping
                                                        p: ({ children }) => {
                                                            return <p className="pa-text-content" style={{maxWidth: '100%', overflowX: 'hidden', wordWrap: 'break-word'}}>{children}</p>;
                                                        },
                                                        // Handle lists with proper spacing
                                                        ul: ({ children }) => {
                                                            return <ul className="pa-list-content" style={{maxWidth: '100%', overflowX: 'hidden'}}>{children}</ul>;
                                                        },
                                                        ol: ({ children }) => {
                                                            return <ol className="pa-list-content" style={{maxWidth: '100%', overflowX: 'hidden'}}>{children}</ol>;
                                                        },
                                                        li: ({ children }) => {
                                                            return <li className="pa-list-item" style={{maxWidth: '100%', overflowX: 'hidden', wordWrap: 'break-word'}}>{children}</li>;
                                                        },
                                                        // Force code blocks to wrap
                                                        code: ({ children, className, inline }) => {
                                                            if (inline) {
                                                                return <code className={className} style={{maxWidth: '100%', wordWrap: 'break-word'}}>{children}</code>;
                                                            }
                                                            return <code className={className} style={{maxWidth: '100%', overflowX: 'hidden', whiteSpace: 'pre-wrap', wordWrap: 'break-word'}}>{children}</code>;
                                                        },
                                                        pre: ({ children }) => {
                                                            return <pre style={{maxWidth: '100%', overflowX: 'hidden', whiteSpace: 'pre-wrap', wordWrap: 'break-word'}}>{children}</pre>;
                                                        },
                                                        // Handle headings with proper wrapping
                                                        h1: ({ children }) => <h1 className="pa-heading" style={{maxWidth: '100%', wordWrap: 'break-word'}}>{children}</h1>,
                                                        h2: ({ children }) => <h2 className="pa-heading" style={{maxWidth: '100%', wordWrap: 'break-word'}}>{children}</h2>,
                                                        h3: ({ children }) => <h3 className="pa-heading" style={{maxWidth: '100%', wordWrap: 'break-word'}}>{children}</h3>,
                                                        h4: ({ children }) => <h4 className="pa-heading" style={{maxWidth: '100%', wordWrap: 'break-word'}}>{children}</h4>,
                                                    }}
                                                >
                                                    {preprocessMathContent(msg.content)}
                                                </ReactMarkdown>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="pa-message-input">
                                        <input
                                            type="text"
                                            placeholder="Ask a follow-up question or request clarification..."
                                            value={currentMessage}
                                            onChange={(e) => setCurrentMessage(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                        />
                                        <button onClick={handleSendMessage}>Send</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="pa-text-input">
                                    <h4 className="ap-section-title">Chat with me</h4>
                                    
                                    {/* Hidden file input */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                    />
                                    
                                    {/* Image upload button */}
                                    <div className="pa-upload-section">
                                        <button
                                            type="button"
                                            onClick={triggerFileInput}
                                            className="pa-upload-image-btn"
                                            title="Upload images"
                                        >
                                            <MdCameraAlt size={20} />
                                            Upload Images
                                        </button>
                                        {uploadedImages.length > 0 && (
                                            <span className="pa-image-count">
                                                {uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''} selected
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Display uploaded images */}
                                    {uploadedImages.length > 0 && (
                                        <div className="pa-uploaded-images-preview">
                                            {uploadedImages.map(image => (
                                                <div key={image.id} className="pa-uploaded-image-item">
                                                    <img
                                                        src={image.url}
                                                        alt={image.name}
                                                        className="pa-uploaded-image-thumb"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeUploadedImage(image.id)}
                                                        className="pa-remove-image-btn"
                                                        title="Remove image"
                                                    >
                                                        ✕
                                                    </button>
                                                    <span className="pa-image-name">{image.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    <textarea
                                        placeholder="Enter your answer or thoughts..."
                                        value={textInput}
                                        onChange={(e) => setTextInput(e.target.value)}
                                        className="pa-response-textarea"
                                    />
                                    <button 
                                        className={`pa-submit-button ${loading ? 'loading' : ''}`}
                                        onClick={handleSubmit} 
                                        disabled={loading}
                                    >
                                        {loading ? 'Generating...' : 'Generate Answer'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                <Feedback />
            </div>
            
            <div className="pa-dashboard-footer">
                <Footer />
            </div>
        </div>
    );
};

export default PersonalizedAnswer;